// Motivo di revisione LEGGIBILE (#5, richiesta Davide 21/07: «ho dovuto
// chiedere cosa volesse dire "VIES NON valido"»).
//
// Il testo che arriva dal backend (``review_reason``) è scritto per chi conosce
// già il dominio fiscale: dice il verdetto, non cosa significa né cosa fare.
// Qui lo traduciamo in tre pezzi separati, che è come si ragiona davanti alla
// riga: **cosa è successo** (etichetta), **perché blocca** (conseguenza reale,
// in euro o in rischio), **cosa fare** (l'azione successiva).
//
// La chiave è ``review_code``, un codice STABILE calcolato dal backend
// (classifier.review_code): il testo libero cambia, il codice no. Un codice
// sconosciuto o assente non è un errore — si mostra il testo grezzo, che resta
// pur sempre meglio di niente.
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { Proposal } from "../types";

interface Spiegazione {
  /** Etichetta corta, in italiano, che sta in una cella di tabella. */
  label: string;
  /** Cosa vuol dire davvero, senza sigle. */
  cosa: string;
  /** L'azione successiva, concreta. */
  fare: string;
  /** «blocco» = non si può emettere così · «conferma» = serve un ok umano ·
   *  «info» (solo il chip del registro) = non ferma niente, è contesto. */
  tono: "blocco" | "conferma" | "info";
}

// Ogni voce nasce da un review_reason reale prodotto dal backend. Il commento
// accanto riporta la frase originale, così si vede subito a cosa corrisponde.
const SPIEGAZIONI: Record<string, Spiegazione> = {
  // «VIES NON valido: conferma con Davide prima di emettere»
  vies_non_valido: {
    label: "P.IVA UE non valida",
    cosa:
      "La partita IVA del cliente non risulta registrata nel VIES, l'archivio europeo delle " +
      "partite IVA abilitate agli scambi fra paesi UE.",
    fare:
      "Senza VIES valido non è una cessione intracomunitaria: se emetti senza IVA, quell'IVA " +
      "resta a carico di BS COM. Se il cliente è un privato la sigla giusta è OSS (con IVA); " +
      "se è un'azienda, fatti dare la P.IVA corretta.",
    tono: "blocco",
  },
  // «Azienda UE: VIES da verificare prima di emettere»
  vies_da_verificare: {
    label: "P.IVA UE da verificare",
    cosa: "Cliente azienda in un altro paese UE, ma la P.IVA non è ancora stata controllata sul VIES.",
    fare: "Lancia «Riclassifica»: l'arricchimento interroga il VIES e scioglie da solo il dubbio.",
    tono: "conferma",
  },
  // «P.IVA non valida per VIES/registro: verifica prima di emettere»
  piva_inattiva: {
    label: "P.IVA non valida",
    cosa: "Il numero non risulta come partita IVA esistente né al VIES né al registro.",
    fare:
      "Una fattura a una P.IVA inesistente viene scartata dallo SdI. Fatti ridare il numero " +
      "corretto; se il cliente è un privato, la sigla giusta è SF.",
    tono: "blocco",
  },
  // «Ente senza P.IVA (ambasciata/consolato/ente): …»
  ente_senza_piva: {
    label: "Ambasciata / ente",
    cosa:
      "Il cliente è un ente senza partita IVA (ambasciata, consolato, ente): il numero che ha " +
      "dato è un codice fiscale. Usato come P.IVA, la fattura viene scartata dallo SdI.",
    fare:
      "Emetti la FA a mano in Eureka: codice fiscale nel suo campo, tipo anagrafico «E», " +
      "codice destinatario 0000000. Senza IVA (art. 72) solo se l'ente ti manda il modulo " +
      "181/US vistato dagli Esteri, altrimenti IVA al 22%.",
    tono: "blocco",
  },
  // «Azienda IT senza P.IVA: recuperala o ripiega su SF»
  azienda_it_senza_piva: {
    label: "Azienda senza P.IVA",
    cosa:
      "Il cliente si è qualificato come azienda (ragione sociale o richiesta di fattura) ma " +
      "non ha lasciato la partita IVA.",
    fare: "Chiedi la P.IVA e scrivila in «Modifica». Se non arriva, emetti una SF come privato.",
    tono: "blocco",
  },
  // «Cod.Dest «0000000» (nessun canale telematico) e nessuna PEC: …»
  sdi_muto: {
    label: "Codice SDI muto",
    cosa:
      "Il codice destinatario è «0000000». Formalmente valido, ma è il codice che dice «non ho " +
      "un canale telematico»: la fattura viene accettata dallo SdI e NON recapitata al cliente, " +
      "resta solo nel suo cassetto fiscale.",
    fare:
      "Recupera il codice destinatario a 7 caratteri o la PEC e scrivili in «Modifica». " +
      "In alternativa, se il cliente è un privato, emetti una SF.",
    tono: "blocco",
  },
  // «SDI/PEC non trovati: recuperali o ripiega su SF»
  // CF_DITTA_MANCANTE (classifier) — Davide 16/10: dati completi alla spedizione.
  cf_ditta_mancante: {
    label: "Manca CF della ditta",
    cosa:
      "È una ditta individuale: in fattura serve il suo codice fiscale personale di 16 caratteri, " +
      "non la P.IVA. Nel registro non c'è.",
    fare:
      "Chiedilo al cliente e inseriscilo in «Modifica» → Codice fiscale. Se all'emissione manca " +
      "ancora, OM fa la SF.",
    tono: "blocco",
  },
  // CF_DITTA_ATTESA_EUREKA (classifier) — 21/10, The Monkeyz: CF valido ma
  // Eureka scollegato, e la cella diceva «Manca CF». Non è un dato mancante.
  cf_ditta_attesa_eureka: {
    label: "CF ok, aspetto Eureka",
    cosa:
      "È una ditta individuale e il codice fiscale c'è ed è valido. Manca solo il controllo " +
      "della sua anagrafica in Eureka, che non ha risposto.",
    fare:
      "Niente da correggere: non cambiare il tipo in SF. OM riprova da solo alla prossima " +
      "emissione; se Eureka è scollegato, premi «Riconnetti Eureka».",
    tono: "blocco",
  },
  // CF_DITTA_ERRATO (classifier) — Davide 19/10, FA 131 Chiocchia.
  cf_ditta_errato: {
    label: "CF della ditta sbagliato",
    cosa:
      "È una ditta individuale e il codice fiscale scritto dal cliente non va: il carattere di " +
      "controllo non torna, oppure il cognome nel codice non è quello del cliente. Così lo SdI " +
      "scarta la fattura o la intesta a un'altra persona.",
    fare:
      "OM cerca da solo il CF giusto (registro, Eureka, altri ordini) e ti avvisa in campanella " +
      "se non lo trova. Se all'emissione manca ancora, OM fa la SF.",
    tono: "blocco",
  },
  sdi_pec_assenti: {
    label: "Manca SDI o PEC",
    cosa:
      "Per una fattura elettronica serve un recapito: il codice destinatario a 7 caratteri " +
      "oppure una PEC. Qui non c'è né l'uno né l'altra.",
    fare: "Chiedi il recapito al cliente e inseriscilo in «Modifica», oppure ripiega su SF.",
    tono: "blocco",
  },
  // «Paese mancante nel billing»
  paese_mancante: {
    label: "Paese mancante",
    cosa:
      "Senza il paese del cliente non si può decidere il documento: è il paese che distingue " +
      "SF (Italia), OSS (privato UE) e le fatture estere.",
    fare: "Scrivi il paese (sigla a 2 lettere, es. IT) in «Modifica».",
    tono: "blocco",
  },
  // ««Company» sembra un dato catastale (Bonus Mobili): probabile privato → SF»
  dato_catastale: {
    label: "Dato catastale, non azienda",
    cosa:
      "Nel campo ragione sociale c'è un riferimento catastale (foglio, particella, CILA): è un " +
      "privato che sta usando il Bonus Mobili, non un'azienda.",
    fare: "È un privato: la sigla giusta è SF. Conferma con «Modifica» o «Riclassifica».",
    tono: "conferma",
  },
  // «Azienda extra-UE: codice IVA art.8 da confermare»
  extra_ue_art8: {
    label: "Extra-UE, art. 8 da confermare",
    cosa:
      "Cliente azienda fuori dall'Unione Europea: è un'esportazione non imponibile art. 8, ma " +
      "il codice IVA da usare va confermato caso per caso.",
    fare: "Conferma il codice IVA prima di emettere; nel dubbio senti il commercialista.",
    tono: "conferma",
  },
  // «Privato extra-UE: classificazione da confermare (commercialista)»
  privato_extra_ue: {
    label: "Privato extra-UE",
    cosa: "Vendita a un privato fuori dall'UE: non rientra in nessuno dei casi automatici.",
    fare: "Da decidere col commercialista, poi imposta la sigla a mano in «Modifica».",
    tono: "conferma",
  },
  // «Importo rimborso da confermare» (nc_detect)
  rimborso_da_confermare: {
    label: "Rimborso da confermare",
    cosa:
      "È stato rilevato un rimborso e proposta una nota di credito, ma l'importo va confermato " +
      "sui dati veri del marketplace.",
    fare:
      "Apri «Conferma rimborso» e controlla quantità e importo sull'ordine originale, non " +
      "sulla stima.",
    tono: "conferma",
  },
  // «Registro Imprese: impresa cessata/sospesa/in liquidazione — …»
  registro_non_attivo: {
    label: "Impresa non più attiva",
    cosa:
      "Al Registro Imprese la società risulta cessata, sospesa o in liquidazione: la partita " +
      "IVA potrebbe non esistere più.",
    fare:
      "Una fattura elettronica a una P.IVA cessata viene scartata dallo SdI. Verifica con il " +
      "cliente; se ha chiuso ed è ormai un privato, la sigla giusta è SF.",
    tono: "blocco",
  },
  // «Ordine era escluso (GSheet NO): conferma prima di fatturare»
  escluso_storico: {
    label: "Era escluso a mano",
    cosa:
      "Nel vecchio foglio questo ordine era segnato NO. Poteva voler dire «già fatturato fuori " +
      "dal portale»: fatturarlo di nuovo creerebbe un doppione.",
    fare:
      "Controlla che non esista già un documento per questo ordine. Se non c'è, «Segna pronto» " +
      "sblocca la riga.",
    tono: "blocco",
  },
};

export function spiegazioneDi(p: Proposal): Spiegazione | null {
  if (!p.review_reason) return null;
  const code = p.review_code ?? null;
  return (code && SPIEGAZIONI[code]) || null;
}

/** Spiegazione del chip «Registro Imprese», che non è un motivo di revisione:
 *  la riga si può fatturare, questo è il contesto per non doverlo cercare. */
const REGISTRO_SPIEGA: Record<string, Spiegazione> = {
  inattiva: {
    label: "Registro Imprese: inattiva",
    cosa:
      "L'impresa è iscritta al Registro Imprese ma non ha dichiarato l'inizio attività al REA. " +
      "Non è la partita IVA a essere spenta: quella risulta valida al VIES.",
    fare:
      "Nulla: la fattura elettronica si emette lo stesso, allo SdI conta che la partita IVA " +
      "esista. È un'informazione, non un blocco.",
    tono: "conferma",
  },
};

function spiegaRegistro(stato: string): Spiegazione {
  return (
    REGISTRO_SPIEGA[stato] ?? {
      label: `Registro Imprese: ${stato}`,
      cosa: `Al Registro Imprese l'impresa risulta «${stato}».`,
      fare: "Verifica con il cliente se la partita IVA è ancora operativa.",
      tono: "conferma",
    }
  );
}

const TONI = {
  blocco: "bg-rose-500/15 text-rose-700 hover:bg-rose-500/25",
  conferma: "bg-orange-500/15 text-orange-700 hover:bg-orange-500/25",
  info: "bg-ink/[0.06] text-ink-2 hover:bg-ink/[0.1]",
} as const;

/** Larghezza del pannello: fissa, perché la posizione si calcola prima di
 *  mostrarlo e deve poter stare dentro il viewport. */
const LARGHEZZA = 330;
/** Stima d'ingombro per decidere sopra/sotto. Meglio una stima che un salto:
 *  misurare dopo il mount farebbe apparire il pannello e poi spostarsi. */
const ALTEZZA_STIMATA = 230;
/** Il mouse deve poter attraversare lo spazio fra chip e pannello. */
const CHIUSURA_MS = 180;
const APERTURA_MS = 90;

/**
 * Cella «Motivo»: etichetta leggibile; passandoci sopra (o cliccandola) si apre
 * la spiegazione completa. Il testo grezzo del backend resta sempre in fondo al
 * pannello, così un codice non mappato non nasconde mai l'informazione.
 *
 * 🔴 Il pannello vive in un PORTAL con `position: fixed`, e non è pignoleria:
 * la tabella è dentro un `overflow-x-auto` e ogni `<td>` è `overflow-hidden`
 * (FatturazionePage), quindi un pannello posizionato in assoluto viene tagliato
 * ai bordi della cella — a schermo si vedeva una striscia bianca alta 20px e
 * nient'altro. Il commento nel codice diceva «fixed» mentre la classe diceva
 * `absolute`: la spiegazione era invisibile da allora.
 */
export function ReviewReasonCell({ proposal }: { proposal: Proposal }) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [fissato, setFissato] = useState(false);
  const ancora = useRef<HTMLButtonElement>(null);
  const timer = useRef(0);

  const chiudi = useCallback(() => {
    window.clearTimeout(timer.current);
    setPos(null);
    setFissato(false);
  }, []);

  const apri = useCallback(() => {
    const r = ancora.current?.getBoundingClientRect();
    if (!r) return;
    const spazioSotto = window.innerHeight - r.bottom;
    setPos({
      top: spazioSotto > ALTEZZA_STIMATA ? r.bottom + 6 : Math.max(12, r.top - ALTEZZA_STIMATA - 6),
      left: Math.max(12, Math.min(r.left, window.innerWidth - LARGHEZZA - 12)),
    });
  }, []);

  // Scroll e resize spostano l'ancora ma non il pannello (è fixed): si chiude,
  // che è anche quello che fa un tooltip nativo. ESC vale sempre, anche fissato.
  useEffect(() => {
    if (!pos) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") chiudi();
    };
    window.addEventListener("scroll", chiudi, true);
    window.addEventListener("resize", chiudi);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", chiudi, true);
      window.removeEventListener("resize", chiudi);
      window.removeEventListener("keydown", onKey);
    };
  }, [pos, chiudi]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const reason = proposal.review_reason;
  const registro = proposal.registro_stato;
  if (!reason && !registro) return <span className="text-ink-2">—</span>;

  // Il motivo di revisione ha la precedenza: è quello che blocca. Lo stato del
  // registro si mostra da solo solo quando non c'è nulla che ferma la riga.
  const spiega = reason ? spiegazioneDi(proposal) : spiegaRegistro(registro as string);
  const tono = reason ? (spiega?.tono ?? "conferma") : "info";
  const etichetta = spiega?.label ?? reason ?? "";

  return (
    <span className="inline-flex max-w-full">
      <button
        ref={ancora}
        type="button"
        onClick={() => {
          if (fissato) return chiudi();
          setFissato(true);
          apri();
        }}
        onMouseEnter={() => {
          window.clearTimeout(timer.current);
          timer.current = window.setTimeout(apri, APERTURA_MS);
        }}
        onMouseLeave={() => {
          window.clearTimeout(timer.current);
          if (!fissato) timer.current = window.setTimeout(() => setPos(null), CHIUSURA_MS);
        }}
        onFocus={apri}
        onBlur={() => !fissato && chiudi()}
        aria-expanded={pos !== null}
        title="Passa sopra o clicca per la spiegazione"
        className={`inline-flex max-w-full items-center gap-1 truncate rounded px-1.5 py-0.5 text-[11px] font-medium transition-colors duration-[120ms] ${TONI[tono]}`}
      >
        <span className="truncate">{etichetta}</span>
        <span aria-hidden="true" className="opacity-60">
          ?
        </span>
      </button>
      {pos !== null &&
        createPortal(
          <>
            {/* Lo sfondo cattura il click SOLO quando il pannello è fissato:
                in hover chiuderebbe la riga sotto al primo click. */}
            {fissato && (
              <button
                type="button"
                aria-label="Chiudi spiegazione"
                className="fixed inset-0 z-[60] cursor-default"
                onClick={chiudi}
              />
            )}
            <div
              role="tooltip"
              style={{ top: pos.top, left: pos.left, width: LARGHEZZA }}
              onMouseEnter={() => window.clearTimeout(timer.current)}
              onMouseLeave={() => !fissato && chiudi()}
              className="fixed z-[61] rounded-lg border border-line bg-card p-3 text-left shadow-popover"
            >
              <p className="text-[12px] font-semibold text-ink">
                {spiega?.label ?? "In revisione"}
              </p>
              {spiega ? (
                <>
                  <p className="mt-1.5 text-[11.5px] leading-snug text-ink-2">{spiega.cosa}</p>
                  <p className="mt-2 text-[11.5px] font-medium leading-snug text-ink">
                    Cosa fare: <span className="font-normal text-ink-2">{spiega.fare}</span>
                  </p>
                </>
              ) : null}
              {reason ? (
                <p className="mt-2 border-t border-line pt-1.5 text-[10.5px] leading-snug text-ink-2">
                  Messaggio originale: {reason}
                </p>
              ) : null}
            </div>
          </>,
          document.body,
        )}
    </span>
  );
}
